import { useEffect, useRef, useState } from "react";
import { api, type Product, type ProductPhotoMatch } from "./api";
import { SelectField } from "./fields";
import { productCode } from "./productCode";
import { productName } from "./productName";
import { shrink, type Taken } from "./pos/photos";
import { Alert, Badge, Modal, Working } from "./ui";

interface PictureRow {
  readonly id: number;
  readonly fileName: string;
  readonly picture?: Taken;
  readonly match?: ProductPhotoMatch;
  readonly productId: string;
  readonly status: "reading" | "review" | "saved";
  readonly error?: string;
}

/** D, 6 October 2026: upload pictures together, check the suggested items, then save. */
export function ProductPictures({ products, onClose, onChanged }: {
  readonly products: readonly Product[];
  readonly onClose: () => void;
  readonly onChanged: () => void;
}): React.ReactElement {
  const [rows, setRows] = useState<PictureRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const previews = useRef<string[]>([]);
  const nextId = useRef(0);
  const active = products.filter((product) => product.isActive);
  useEffect(() => () => { for (const preview of previews.current) URL.revokeObjectURL(preview); }, []);

  function update(id: number, change: Partial<PictureRow>): void {
    setRows((current) => current.map((row) => row.id === id ? { ...row, ...change } : row));
  }

  async function read(id: number, picture: Taken): Promise<void> {
    update(id, { status: "reading", error: "" });
    try {
      const { match } = await api.matchProductPhoto(picture);
      // A weak suggestion stays unselected; the person can still choose it explicitly.
      const best = match.candidates[0];
      update(id, { match, productId: best?.confidence === "high" ? best.productId : "", status: "review" });
    } catch (problem) {
      update(id, { status: "review", error: problem instanceof Error ? problem.message : "Could not match this picture" });
    }
  }

  async function add(files: File[]): Promise<void> {
    if (!files.length || busy) return;
    if (rows.length + files.length > 10) { setError("Choose up to 10 pictures at a time. Remove a picture to make room."); return; }
    setError(undefined);
    setBusy(true);
    try {
      // Read one at a time so a batch does not flood OHC's API account.
      for (const file of files) {
        const id = ++nextId.current;
        setRows((current) => [...current, { id, fileName: file.name, productId: "", status: "reading" }]);
        try {
          if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) throw new Error("Use a JPEG, PNG or WebP picture.");
          const picture = await shrink(file);
          previews.current.push(picture.preview);
          update(id, { picture });
          await read(id, picture);
        } catch (problem) {
          update(id, { status: "review", error: problem instanceof Error ? problem.message : "That picture could not be opened" });
        }
      }
    } finally { setBusy(false); }
  }

  const selected = rows.filter((row) => row.status !== "saved" && row.picture && active.some((product) => product.id === row.productId));
  async function save(): Promise<void> {
    if (busy || !selected.length) return;
    const ids = selected.map((row) => row.productId);
    if (new Set(ids).size !== ids.length) {
      setError("Two pictures are selected for the same product. Keep one, or choose a different product before saving.");
      return;
    }
    setError(undefined);
    setBusy(true);
    setSaving(true);
    let saved = 0;
    try {
      for (const row of selected) {
        if (!row.picture) continue;
        try {
          await api.setProductPicture(row.productId, row.picture.contentType, row.picture.data);
          update(row.id, { status: "saved", error: "" });
          saved++;
        } catch (problem) {
          update(row.id, { error: problem instanceof Error ? problem.message : "Could not save this picture" });
        }
      }
    } finally {
      setBusy(false);
      setSaving(false);
      if (saved) onChanged();
    }
  }

  return <Modal open title="Match product pictures" size="lg" onClose={() => { if (!busy) onClose(); }} footer={<>
    <button type="button" className="ti-btn ti-btn-light" disabled={busy} onClick={onClose}>Close</button>
    <button type="button" className="ti-btn ti-btn-primary-full" disabled={busy || !selected.length} onClick={() => void save()}>
      {saving ? <Working label="Saving pictures" /> : `Save selected pictures${selected.length ? ` (${selected.length})` : ""}`}
    </button>
  </>}>
    <p>Choose up to 10 pictures, one product per picture. Check the suggested product, language and packaging before saving.</p>
    {error ? <Alert tone="danger">{error}</Alert> : null}
    <label className={`ti-btn ti-btn-light cursor-pointer ${busy ? "opacity-60" : ""}`} htmlFor="product-pictures-upload">
      <i className="ri-image-add-line" /> {busy && !saving ? "Matching pictures" : "Choose pictures"}
      <input id="product-pictures-upload" type="file" className="sr-only" accept="image/jpeg,image/png,image/webp" multiple disabled={busy} onChange={(event) => {
        const files = Array.from(event.target.files ?? []); event.target.value = ""; void add(files);
      }} />
    </label>
    {rows.map((row) => {
      const chosen = products.find((product) => product.id === row.productId);
      const candidates = (row.match?.candidates ?? []).filter((candidate) => active.some((product) => product.id === candidate.productId));
      const ordered = [...candidates.map((candidate) => active.find((product) => product.id === candidate.productId)!), ...active.filter((product) => !candidates.some((candidate) => candidate.productId === product.id))];
      return <div key={row.id} className="border border-defaultborder rounded-md p-3 mt-3" data-picture-row={row.id}>
        <div className="flex gap-3 flex-col sm:flex-row">
          {row.picture ? <a href={row.picture.preview} target="_blank" rel="noreferrer" className="shrink-0">
            <img src={row.picture.preview} alt={row.fileName} className="w-32 h-32 object-contain bg-light rounded-sm" />
          </a> : null}
          <div className="grow min-w-0">
            <div className="flex justify-between gap-2 mb-2">
              <span className="font-semibold break-words">{row.fileName}</span>
              {row.status === "saved" ? <Badge tone="success">Saved</Badge> : <button type="button" className="ti-btn ti-btn-light !mb-0" disabled={busy} aria-label={`Remove ${row.fileName}`} onClick={() => setRows((current) => current.filter((one) => one.id !== row.id))}>Remove</button>}
            </div>
            {row.status === "reading" ? <Working label="Matching picture" /> : <>
              {row.match?.description ? <p className="mb-1">{row.match.description}</p> : null}
              {row.match ? <p className="text-[0.8125rem] mb-2">{row.match.note}</p> : null}
              {row.error ? <Alert tone="danger">{row.error}</Alert> : null}
              {candidates.length ? <ul className="text-[0.8125rem] mb-2 space-y-1">{candidates.map((candidate) => <li key={candidate.productId}>
                <strong>{productName(active.find((product) => product.id === candidate.productId)!)}</strong> ({candidate.confidence === "high" ? "Likely match" : candidate.confidence === "medium" ? "Possible match" : "Uncertain match"}): {candidate.reason}
              </li>)}</ul> : null}
              {row.picture ? <>
                <label htmlFor={`picture-product-${row.id}`} className="form-label">Product for this picture</label>
                <SelectField id={`picture-product-${row.id}`} value={row.productId} disabled={busy || row.status === "saved"} onChange={(productId) => update(row.id, { productId, error: "" })} options={[
                  { value: "", label: "Skip this picture" },
                  ...ordered.map((product) => ({ value: product.id, label: `${productCode(product)} ${productName(product)}` })),
                ]} />
                {chosen?.photoId && row.status !== "saved" ? <div className="flex items-center gap-2 mt-2 text-[0.8125rem]">
                  <img src={`/api/photos/${chosen.photoId}`} alt="Current product picture" className="w-12 h-12 object-contain" />
                  Saving will replace the current product picture. The old picture stays on the record.
                </div> : null}
                {row.status !== "saved" ? <button type="button" className="ti-btn ti-btn-light mt-2 !mb-0" disabled={busy} onClick={() => {
                  setBusy(true); void read(row.id, row.picture!).finally(() => setBusy(false));
                }}>Try matching again</button> : null}
              </> : null}
            </>}
          </div>
        </div>
      </div>;
    })}
    {!rows.length ? <p className="text-[0.8125rem] mt-2">Pictures are saved to the catalogue only when you press Save selected pictures.</p> : null}
  </Modal>;
}
